import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Printer, ArrowLeft, Phone, Mail, Globe, MapPin, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { sendViaEmail, sendViaWhatsApp, uploadDocument, buildWhatsAppUrl } from "@/lib/pdfSend";
import { docTexts } from "@/lib/docTexts";

const COMPANY = {
  name: "BLU LODGE PVT LTD",
  address: "G. Moonimaage, 8th floor North Wing · Dharumavantha Magu · 20128 K. Malé · Maldives",
  phone: "+960 9356335",
  email: "info@blulodgemaldives.com",
  website: "https://www.blulodgemaldives.com",
};

const titleFor = (g) => {
  const s = (g.sex || "").toUpperCase();
  if (s.startsWith("F")) return "MRS";
  if (s.startsWith("M")) return "MR";
  return "";
};

export default function Voucher() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoDone = useRef(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${id}/full`).then((r) => setData(r.data)).catch(() => setError("Prenotazione non trovata"));
  }, [id]);

  useEffect(() => {
    if (autoDone.current || !data) return;
    const s = searchParams.get("send");
    if (s !== "email" && s !== "whatsapp") return;
    autoDone.current = true;
    const bk = data.booking;
    (async () => {
      setSending(true);
      try {
        await new Promise((r) => setTimeout(r, 400));
        const el = document.querySelector('[data-testid="voucher-sheet"]');
        if (!el) { setSending(false); return; }
        if (s === "email") {
          const email = bk.guest_email || window.prompt("Email destinatario:") || "";
          if (email) { await sendViaEmail(bk.id, "voucher", el, email); toast.success(`Voucher inviato a ${email}`); }
        } else {
          const phone = bk.guest_phone || window.prompt("Numero WhatsApp (con prefisso, es. +39...):") || "";
          if (phone) {
            const d = await uploadDocument(bk.id, "voucher", el);
            toast.success("Apertura WhatsApp…");
            window.location.href = buildWhatsAppUrl(d.public_url, "voucher", phone, `${bk.guest_name} ${bk.guest_surname}`);
          }
        }
      } catch (e) { toast.error("Invio non riuscito"); }
      setSending(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchParams]);

  if (error) return <div className="p-10 text-center text-slate-500">{error}</div>;
  if (!data) return <div className="p-10 text-center text-slate-400">Caricamento…</div>;

  const { booking: b, structure, room, nights, settings } = data;
  const t = docTexts(settings);
  const guests = b.guests || [];
  const ref = (b.id || "").slice(0, 8).toUpperCase();

  const doSend = async (channel) => {
    if (sending) return;
    const el = document.querySelector('[data-testid="voucher-sheet"]');
    if (!el) return;
    setSending(true);
    try {
      if (channel === "email") {
        let email = b.guest_email;
        if (!email) email = window.prompt("Email destinatario:") || "";
        if (!email) { setSending(false); return; }
        await sendViaEmail(b.id, "voucher", el, email);
        toast.success(`Voucher inviato a ${email}`);
      } else {
        let phone = b.guest_phone;
        if (!phone) phone = window.prompt("Numero WhatsApp (con prefisso, es. +39...):") || "";
        if (!phone) { setSending(false); return; }
        await sendViaWhatsApp(b.id, "voucher", el, phone, `${b.guest_name} ${b.guest_surname}`);
        toast.success("Apertura WhatsApp…");
      }
    } catch (e) {
      toast.error("Invio non riuscito");
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <style>{`@media print { .no-print { display: none !important; } body { background: white; } .sheet { box-shadow: none !important; margin: 0 !important; } }`}</style>

      <div className="max-w-3xl mx-auto mb-4 flex justify-between no-print">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft size={16} /> Indietro
        </button>
        <div className="flex gap-2">
          <button onClick={() => doSend("email")} disabled={sending} data-testid="send-voucher-email" className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-3 py-2 rounded-md disabled:opacity-50">
            <Mail size={16} /> Email
          </button>
          <button onClick={() => doSend("whatsapp")} disabled={sending} data-testid="send-voucher-whatsapp" className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-3 py-2 rounded-md disabled:opacity-50">
            <MessageCircle size={16} /> WhatsApp
          </button>
          <button onClick={() => window.print()} data-testid="print-voucher" className="flex items-center gap-2 bg-sky-700 hover:bg-sky-800 text-white text-sm font-semibold px-4 py-2 rounded-md">
            <Printer size={16} /> Stampa
          </button>
        </div>
      </div>

      <div className="sheet max-w-3xl mx-auto bg-white shadow-sm overflow-hidden" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="voucher-sheet">
        {/* Company header */}
        <div className="bg-slate-900 text-white px-8 py-6 flex items-center gap-5">
          <img src="/blu-lodge-logo.png" alt="Blu Lodge Maldives" className="w-20 h-20 object-contain bg-white rounded-lg p-2 shrink-0" data-testid="voucher-logo" />
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>{COMPANY.name}</h1>
            <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-[11px] text-slate-300">
              <div className="flex items-center gap-1.5"><MapPin size={12} /> {COMPANY.address}</div>
              <div className="flex items-center gap-1.5"><Phone size={12} /> {COMPANY.phone}</div>
              <div className="flex items-center gap-1.5"><Mail size={12} /> {COMPANY.email}</div>
              <div className="flex items-center gap-1.5"><Globe size={12} /> {COMPANY.website.replace("https://", "")}</div>
            </div>
          </div>
        </div>

        <div className="px-8 py-6">
          {/* Booking reference */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-b-2 border-slate-800 pb-2">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Booking Reference</div>
            <div className="font-mono font-bold text-slate-900">#{ref}</div>
          </div>

          {/* Guests */}
          <div className="py-5 space-y-1">
            {guests.length === 0 && <div className="text-slate-400 text-sm">Nessun ospite</div>}
            {guests.map((g, i) => (
              <div key={i} className="text-xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {[titleFor(g), (g.name || "").toUpperCase(), (g.surname || "").toUpperCase()].filter(Boolean).join(" ") || "—"}
              </div>
            ))}
          </div>

          {/* Stay details */}
          <div className="rounded-md border border-slate-200 overflow-hidden">
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 font-bold text-slate-800 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {structure.name || "Struttura"}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 p-4 text-sm">
              <Info label="Camera" value={`${room.name || "—"}${room.type ? ` · ${room.type}` : ""}`} />
              <Info label="Ospiti" value={`${b.adults || 0} adulti · ${b.children || 0} bambini`} />
              <Info label="Notti" value={nights} />
              <Info label="Check-in" value={`${b.checkin} ${b.checkin_time || ""}`} />
              <Info label="Check-out" value={`${b.checkout} ${b.checkout_time || ""}`} />
              <Info label="Volo" value={b.flight || "—"} />
            </div>
          </div>

          {/* Check-in / out policy */}
          <div className="mt-4 flex flex-wrap gap-x-8 gap-y-1 text-xs text-slate-600">
            <span><b className="text-slate-800">Check-in:</b> dalle ore 14:00 alle ore 18:00</span>
            <span><b className="text-slate-800">Check-out:</b> fino alle ore 10:00</span>
            <span><b className="text-slate-800">Età minima consentita:</b> 5 anni</span>
          </div>
          {b.card_payments_enabled && b.pay_token && (
            <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-slate-700" data-testid="voucher-pay-online">
              <b className="text-sky-800">💳 Paga online con carta / Pay online by card:</b>{" "}
              <a href={`${window.location.origin}/paga/${b.id}?t=${b.pay_token}`} className="text-sky-700 underline break-all">{`${window.location.origin}/paga/${b.id}?t=${b.pay_token}`}</a>
            </div>
          )}

          {/* Guest registration table */}
          {guests.length > 0 && (
            <div className="mt-6 border border-slate-200 rounded-md overflow-x-auto">
              <div className="px-4 py-2 font-bold text-slate-800 border-b border-slate-200 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Registrazione ospiti
              </div>
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="text-left text-xs uppercase text-slate-500 bg-slate-50">
                    <th className="px-4 py-2">Nome</th>
                    <th className="px-4 py-2">Passaporto</th>
                    <th className="px-4 py-2">Nascita</th>
                    <th className="px-4 py-2">Nazionalità</th>
                    <th className="px-4 py-2">Scadenza</th>
                  </tr>
                </thead>
                <tbody>
                  {guests.map((g, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-4 py-2 font-medium text-slate-800">{`${g.name || ""} ${g.surname || ""}`.trim() || "—"}</td>
                      <td className="px-4 py-2 text-slate-600 font-mono text-xs">{g.passport_number || "—"}</td>
                      <td className="px-4 py-2 text-slate-600">{g.birth_date || "—"}</td>
                      <td className="px-4 py-2 text-slate-600">{g.nationality || "—"}</td>
                      <td className="px-4 py-2 text-slate-600">{g.expiry_date || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Privacy */}
          <p className="mt-6 pt-4 border-t border-slate-200 text-[10px] text-slate-400 leading-relaxed text-justify">{t.privacy}</p>
          <p className="mt-3 text-[11px] font-semibold text-slate-600 leading-relaxed">{t.nb}</p>

          {/* Footer */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
            <span className="flex items-center gap-1.5"><Phone size={12} /> {COMPANY.phone}</span>
            <span className="flex items-center gap-1.5"><Mail size={12} /> {COMPANY.email}</span>
            <a href={COMPANY.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sky-700 underline">
              <Globe size={12} /> {COMPANY.website.replace("https://", "")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="font-semibold text-slate-800">{value}</div>
    </div>
  );
}
